import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { getCoupons, getOffers, getMerchantProducts } from '../api/deals_service';
import { useNavigationStack } from '../contexts/NavigationContext';
import './Deals.css';

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: '1', label: 'Apparel' },
  { value: '3', label: "Men's Apparel" },
  { value: '4', label: "Women's Apparel" },
  { value: '25', label: 'Shoes' },
  { value: '21', label: 'Jewelry & Accessories' },
  { value: '31', label: 'Babies & Kids' },
  { value: '27', label: 'Sports & Fitness' },
  { value: '12', label: 'Department Store' },
  { value: '16', label: 'Gifts' },
  { value: '20', label: 'House wares' },
  { value: '24', label: 'Services' },
];

const Deals = () => {
  const navigate = useNavigate();
  const { navigateWithTransition } = useNavigationStack();
  const { currentUser } = useAuth();

  // Tab state
  const [activeTab, setActiveTab] = useState('coupons');

  // Data states
  const [coupons, setCoupons] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [keyword, setKeyword] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Notification state
  const [notification, setNotification] = useState(null);

  // Merchant product images cache
  const [merchantImages, setMerchantImages] = useState({});

  // Redirect unauthenticated users
  useEffect(() => {
    if (!currentUser) {
      navigate('/auth', { state: { intendedAction: 'deals', message: 'Sign in to access exclusive deals and coupons' } });
    }
  }, [currentUser, navigate]);

  // Fetch data when tab, filters, or page changes
  const fetchData = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      if (activeTab === 'coupons') {
        const result = await getCoupons({
          keyword: keyword || undefined,
          category: category || undefined,
          page,
          limit: 20,
        });
        if (result.success) {
          setCoupons(prev => page === 1 ? result.coupons : [...prev, ...result.coupons]);
          setTotalPages(result.total_pages || 1);
          // Fetch merchant images for coupons that have merchant_id
          fetchMerchantImages(result.coupons);
        } else {
          setError(result.error || 'Failed to fetch coupons');
        }
      } else {
        const result = await getOffers({
          category: category || undefined,
          promotion_type: activeTab === 'promos' ? 'percentage' : undefined,
          page,
          limit: 20,
        });
        if (result.success) {
          setOffers(prev => page === 1 ? result.offers : [...prev, ...result.offers]);
          setTotalPages(result.total_pages || 1);
          fetchMerchantImages(result.offers);
        } else {
          setError(result.error || 'Failed to fetch offers');
        }
      }
    } catch (err) {
      console.error('Failed to fetch deals:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load deals. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, activeTab, keyword, category, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch merchant product images (fire-and-forget enrichment)
  const fetchMerchantImages = async (items) => {
    const merchantIds = [...new Set(
      items
        .filter(item => item.merchant_id && !merchantImages[item.merchant_id])
        .map(item => item.merchant_id)
    )];

    for (const mid of merchantIds.slice(0, 5)) { // Limit to 5 concurrent
      try {
        const result = await getMerchantProducts(mid, 3);
        if (result.success && result.products.length > 0) {
          setMerchantImages(prev => ({ ...prev, [mid]: result.products }));
        }
      } catch (e) {
        // Silent fail for enrichment
        console.debug(`Merchant image fetch failed for ${mid}:`, e.message);
      }
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    // fetchData will be triggered by useEffect
  };

  // Handle tab switch
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setCoupons([]);
    setOffers([]);
  };

  // Copy coupon code to clipboard
  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setNotification({ message: `Code "${code}" copied!`, type: 'success' });
      setTimeout(() => setNotification(null), 3000);
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  if (!currentUser) return null;

  const currentItems = activeTab === 'coupons' ? coupons : offers;
  const hasMore = page < totalPages;

  return (
    <div className="deals-page">
      <div className="deals-container">
        {/* Header */}
        <motion.div
          className="deals-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link to="/" className="back-button" aria-label="Go back to home">
            ← Back to Home
          </Link>
          <h1 className="deals-title">Deals</h1>
          <p className="deals-subtitle">
            Exclusive coupons and offers from top fashion brands
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="deals-tabs">
          <button
            className={`deals-tab ${activeTab === 'coupons' ? 'active' : ''}`}
            onClick={() => handleTabSwitch('coupons')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
              <line x1="7" y1="7" x2="7.01" y2="7" />
            </svg>
            Coupons
          </button>
          <button
            className={`deals-tab ${activeTab === 'promos' ? 'active' : ''}`}
            onClick={() => handleTabSwitch('promos')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
            Promos
          </button>
        </div>

        {/* Filters */}
        <motion.form
          className="deals-filters"
          onSubmit={handleSearch}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="filter-group">
            <input
              type="text"
              className="filter-input"
              placeholder={activeTab === 'coupons' ? 'Search coupons...' : 'Search offers...'}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
            <select
              className="filter-select"
              value={category}
              onChange={(e) => { setCategory(e.target.value); setPage(1); }}
            >
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
            <button type="submit" className="filter-btn">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              Search
            </button>
          </div>
        </motion.form>

        {/* Error */}
        {error && (
          <motion.div
            className="deals-error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <p>⚠️ {error}</p>
            <button onClick={fetchData} className="retry-btn">Try Again</button>
          </motion.div>
        )}

        {/* Loading */}
        {loading && page === 1 && (
          <div className="deals-loading">
            <div className="loading-spinner" />
            <p>Loading {activeTab}...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && currentItems.length === 0 && !error && (
          <motion.div
            className="deals-empty"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="empty-icon">🏷️</div>
            <h2>No {activeTab} found</h2>
            <p>Try adjusting your filters or check back later for new deals!</p>
          </motion.div>
        )}

        {/* Deal Cards Grid */}
        {currentItems.length > 0 && (
          <motion.div
            className="deals-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {currentItems.map((item, index) => (
              <DealCard
                key={item.id || index}
                deal={item}
                type={activeTab}
                onCopyCode={copyCode}
                merchantProducts={merchantImages[item.merchant_id]}
                variants={itemVariants}
              />
            ))}
          </motion.div>
        )}

        {/* Load More */}
        {hasMore && !loading && currentItems.length > 0 && (
          <div className="deals-load-more">
            <button
              className="load-more-btn"
              onClick={() => setPage(prev => prev + 1)}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load More Deals'}
            </button>
          </div>
        )}

        {/* Loading more indicator */}
        {loading && page > 1 && (
          <div className="deals-loading-more">
            <div className="loading-spinner small" />
            <span>Loading more...</span>
          </div>
        )}

        {/* Notification */}
        <AnimatePresence>
          {notification && (
            <motion.div
              className={`deals-notification ${notification.type}`}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
            >
              {notification.message}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};


/**
 * Individual Deal Card Component
 */
const DealCard = ({ deal, type, onCopyCode, merchantProducts, variants }) => {
  const code = deal.coupon_code;
  const url = type === 'coupons' ? deal.coupon_url : deal.offer_url;
  const hasImage = deal.image_url;
  const hasProducts = merchantProducts && merchantProducts.length > 0;

  // Format expiry date
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const expiryDate = formatDate(deal.end_date);
  const isExpiringSoon = deal.end_date && (() => {
    try {
      const d = new Date(deal.end_date);
      const daysLeft = (d - new Date()) / (1000 * 60 * 60 * 24);
      return daysLeft >= 0 && daysLeft <= 7;
    } catch { return false; }
  })();

  return (
    <motion.div className="deal-card" variants={variants} whileHover={{ y: -6, transition: { duration: 0.3 } }}>
      {/* Image Section */}
      <div className="deal-card-visual">
        {hasImage ? (
          <img src={deal.image_url} alt={deal.merchant_name || 'Deal'} className="deal-image" onError={(e) => { e.target.style.display = 'none'; }} />
        ) : hasProducts ? (
          <div className="deal-product-grid">
            {merchantProducts.slice(0, 3).map((p, i) => (
              <img key={i} src={p.image_url} alt={p.name || 'Product'} className="deal-product-thumb" onError={(e) => { e.target.style.display = 'none'; }} />
            ))}
          </div>
        ) : (
          <div className="deal-placeholder-icon">
            {type === 'coupons' ? '🏷️' : '✨'}
          </div>
        )}

        {/* Badges */}
        <div className="deal-badges">
          {deal.discount && (
            <span className="deal-badge discount">{deal.discount}</span>
          )}
          {isExpiringSoon && (
            <span className="deal-badge expiring">Expiring Soon</span>
          )}
          {deal.offer_type && (
            <span className="deal-badge type">{deal.offer_type}</span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="deal-card-content">
        {deal.merchant_name && (
          <span className="deal-merchant">{deal.merchant_name}</span>
        )}
        <h3 className="deal-title">{deal.title || deal.description || 'Special Offer'}</h3>
        {deal.description && deal.description !== deal.title && (
          <p className="deal-description">{deal.description}</p>
        )}
        {deal.category && (
          <span className="deal-category">{deal.category}</span>
        )}
        {expiryDate && (
          <span className="deal-expiry">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            Expires {expiryDate}
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="deal-card-actions">
        {code && (
          <button className="coupon-code-btn" onClick={() => onCopyCode(code)} title="Click to copy code">
            <span className="code-text">{code}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </button>
        )}
        {url && (
          <a href={url} target="_blank" rel="noopener noreferrer" className="deal-shop-btn">
            Shop Now →
          </a>
        )}
      </div>
    </motion.div>
  );
};

export default Deals;

